import { useState } from 'react'
import { CheckCircle, HandPalm, Scales } from '@phosphor-icons/react'
import { useAuth } from '@/auth/context'
import { canOverrideTwoPerson } from '@/auth/roles'
import { useFormat } from '@/i18n'
import { Banner, Field, Select, Spinner, TextArea } from '@/ui'
import {
  DEFAULT_RESOLUTION_CODES,
  NEVER_WAIVED,
  RESOLUTION_CODES,
  RUN_CAUSE_CODES,
  type RunDecision,
  useResolveException,
  useWaiveException,
} from '../api/exceptions'
import { useLot } from '../api/gate'
import { arr } from '../api/mappers'
import { ActionButton } from './Can'
import { ConfirmDialog } from './ConfirmDialog'
import { CorrectionDialog } from './CorrectionDialog'
import { PhotoViewer } from './PhotoViewer'
import { Timeline } from './Timeline'
import { UserName } from './bits'

export interface ExceptionLike {
  id: string
  type: string
  status: string
  subjectType: string
  subjectId: string
  raisedById?: string | null
  detail: Record<string, unknown>
}

/**
 * Resolve / Waive with the role rules (exceptions:resolve / exceptions:waive, two-person rule R11:
 * whoever raised it cannot close it — the server also refuses the day's operator and the lot's
 * committer). DISPUTE is never waived and settles through the dispute panel.
 */
export function ExceptionActions({ exception, size = 'sm' }: { exception: ExceptionLike; size?: 'sm' | 'md' }) {
  const { user } = useAuth()
  const [dialog, setDialog] = useState<'resolve' | 'waive' | null>(null)
  const resolve = useResolveException()
  const waive = useWaiveException()
  if (exception.status !== 'OPEN') return null
  if (exception.type === 'DISPUTE') return <DisputeSettlePanel exception={exception} />
  if (exception.type === 'BALANCE_OUT_OF_BAND' && exception.subjectType === 'RUN')
    return <RunDecisionPanel exception={exception} size={size} />
  const own =
    user && exception.raisedById === user.id && !canOverrideTwoPerson(user.roles)
      ? 'You raised this exception: a second person must close it.'
      : null
  const codes =
    exception.subjectType === 'RUN'
      ? RUN_CAUSE_CODES
      : (RESOLUTION_CODES[exception.type] ?? DEFAULT_RESOLUTION_CODES)
  return (
    <>
      <div className="flex flex-wrap gap-2">
        <ActionButton perm="exceptions:resolve" size={size} leadingIcon={<CheckCircle />} blockedReason={own} onClick={() => setDialog('resolve')}>
          Resolve
        </ActionButton>
        {!NEVER_WAIVED.has(exception.type) && (
          <ActionButton perm="exceptions:waive" size={size} variant="secondary" leadingIcon={<HandPalm />} blockedReason={own} onClick={() => setDialog('waive')}>
            Waive
          </ActionButton>
        )}
      </div>
      <ResolveDialog
        open={dialog === 'resolve'}
        onClose={() => setDialog(null)}
        codes={codes}
        loading={resolve.isPending}
        onConfirm={(resolutionCode, note) => resolve.mutateAsync({ id: exception.id, resolutionCode, note }).then(() => setDialog(null))}
      />
      <ConfirmDialog
        open={dialog === 'waive'}
        onClose={() => setDialog(null)}
        title="Waive this exception"
        description="Waiving accepts the risk without fixing the cause. It is recorded with your name."
        confirmLabel="Waive"
        tone="danger"
        loading={waive.isPending}
        reason={{ label: 'Why it is accepted', minLength: 5 }}
        onConfirm={(reason) => waive.mutateAsync({ id: exception.id, reason }).then(() => setDialog(null))}
      />
    </>
  )
}

function ResolveDialog({
  open,
  onClose,
  codes,
  loading,
  onConfirm,
}: {
  open: boolean
  onClose: () => void
  codes: string[]
  loading: boolean
  onConfirm: (code: string, note: string) => Promise<unknown>
}) {
  const [note, setNote] = useState('')
  return (
    <ConfirmDialog
      open={open}
      onClose={onClose}
      title="Resolve this exception"
      description="Say what settled it. The code is kept on the exception and in the diary."
      confirmLabel="Resolve"
      loading={loading}
      reason={{
        label: 'Resolution',
        choices: codes.map((c) => ({ value: c, label: c.replace(/_/g, ' ').toLowerCase() })),
        allowOther: true,
        asCode: true,
        pattern: /^[A-Z0-9_]+$/,
      }}
      onConfirm={(code) => onConfirm(code, note)}
    >
      <label className="flex flex-col gap-1.5">
        <span className="text-[14px] font-semibold">Note</span>
        <textarea
          className="w-full rounded-chip border border-rule bg-surface px-3 py-2 text-[15px] focus-visible:border-amber focus-visible:outline-none"
          rows={2}
          value={note}
          onChange={(e) => setNote(e.target.value)}
          placeholder="Optional"
        />
      </label>
    </ConfirmDialog>
  )
}

const RUN_DECISIONS: Array<{ value: RunDecision; label: string; help: string }> = [
  {
    value: 'CORRECTED',
    label: 'Corrected — re-check the balance',
    help: 'The run was fixed (a weight re-taken, a sack added). It closes only if it balances now.',
  },
  {
    value: 'ACCEPT_SHORTFALL',
    label: 'Accept the loss',
    help: 'The loss or low yield is real and accepted. Never for a surplus or mixing.',
  },
  {
    value: 'MOVE_TO_CONV',
    label: 'Move the outputs to ordinary',
    help: 'Every batch and sack of this run leaves the brand stream. Use for mixing or a surplus.',
  },
]

/**
 * A run out of balance (F6-B1.c-a): the supervisor names the cause and decides. Same rules as the
 * phone's Investigate — process-runs:correct, never the run's operator or cleanout person (R11).
 */
function RunDecisionPanel({ exception, size }: { exception: ExceptionLike; size: 'sm' | 'md' }) {
  const { user } = useAuth()
  const resolve = useResolveException()
  const [open, setOpen] = useState(false)
  const [decision, setDecision] = useState<RunDecision>('ACCEPT_SHORTFALL')
  const [note, setNote] = useState('')
  const d = exception.detail
  const kinds = arr<string>(d.kinds).map(String)
  const surplusOrMixing = kinds.includes('SURPLUS')
  const own =
    user && exception.raisedById === user.id && !canOverrideTwoPerson(user.roles)
      ? 'You raised this exception: a second person must close it.'
      : null
  const figures: Array<[string, unknown]> = [
    ['In', d.input_kg],
    ['Out', d.output_kg],
    ['Unaccounted', d.residual_kg],
    ['Unaccounted %', d.residual_pct],
    ['Loss limit %', d.loss_limit_pct],
    ['Yield %', d.yield_pct],
    ['Band %', d.min_pct != null ? `${String(d.min_pct)}–${String(d.max_pct)}` : 'no band'],
  ]
  const help = RUN_DECISIONS.find((x) => x.value === decision)?.help
  return (
    <div className="flex flex-col gap-3">
      <div className="text-[14px]">
        <div className="eyebrow mb-1">Why the run is held</div>
        <div>{kinds.length > 0 ? kinds.join(' · ').toLowerCase().replace(/_/g, ' ') : 'out of balance'}</div>
        <dl className="mt-2 grid grid-cols-2 gap-x-4 gap-y-1 font-mono tabular text-[13px] sm:grid-cols-4">
          {figures.map(([k, v]) => (
            <div key={k}>
              <dt className="text-muted">{k}</dt>
              <dd>{v == null || v === '' ? '—' : String(v)}</dd>
            </div>
          ))}
        </dl>
        {kinds.includes('YIELD') && d.min_pct == null && (
          <Banner tone="warn">
            No yield band was set for this crop, step and machine. Add one under Yield bands so later runs are judged
            on it.
          </Banner>
        )}
      </div>
      <div>
        <ActionButton perm="exceptions:resolve" size={size} leadingIcon={<Scales />} blockedReason={own} onClick={() => setOpen(true)}>
          Decide run
        </ActionButton>
      </div>
      <ConfirmDialog
        open={open}
        onClose={() => setOpen(false)}
        title="Decide this run"
        description="Name the cause and what happens to the run. It is recorded with your name."
        confirmLabel="Save decision"
        tone={decision === 'MOVE_TO_CONV' ? 'danger' : 'primary'}
        loading={resolve.isPending}
        disabledReason={
          decision === 'ACCEPT_SHORTFALL' && surplusOrMixing
            ? 'A surplus cannot be accepted: correct it or move the outputs to ordinary.'
            : null
        }
        reason={{
          label: 'Cause',
          choices: RUN_CAUSE_CODES.map((c) => ({ value: c, label: c.replace(/_/g, ' ').toLowerCase() })),
          asCode: true,
          pattern: /^[A-Z0-9_]+$/,
        }}
        onConfirm={(cause) =>
          resolve
            .mutateAsync({ id: exception.id, resolutionCode: cause, note, decision })
            .then(() => setOpen(false))
        }
      >
        <Field label="Decision" required hint={help}>
          <Select
            options={RUN_DECISIONS.map((x) => ({ value: x.value, label: x.label }))}
            value={decision}
            onChange={setDecision}
          />
        </Field>
        <Field label="Note">
          <TextArea rows={2} value={note} onChange={(e) => setNote(e.target.value)} placeholder="What was checked" />
        </Field>
      </ConfirmDialog>
    </div>
  )
}

/**
 * Dispute · Settle (F4-B2.d): every weight of the disputed bag in order with who took it, the
 * re-check photos, what counts now, and the two decisions — Value stands (resolve
 * SUPERVISOR_UPHELD with a note) or Correct weight (the correction screen, settling the dispute).
 */
export function DisputeSettlePanel({ exception }: { exception: ExceptionLike }) {
  const fmt = useFormat()
  const { user } = useAuth()
  const lotQ = useLot(exception.subjectType === 'UNIT' ? exception.subjectId : undefined)
  const resolve = useResolveException()
  const [dialog, setDialog] = useState<'stands' | 'correct' | null>(null)
  const d = exception.detail
  const containerId = typeof d.container_id === 'string' ? d.container_id : undefined
  const photos = arr<string>(d.photo_ids).map(String)
  const lot = lotQ.data
  const bag = lot?.bags.find((b) => b.id === containerId) ?? null
  const operatorIsMe = Boolean(user && lot?.sessionOperatorId === user.id && !canOverrideTwoPerson(user.roles))
  const blocked = operatorIsMe ? "You ran this delivery's day: another supervisor settles its dispute." : null

  return (
    <div className="flex flex-col gap-4">
      <div>
        <div className="eyebrow mb-2">All values{bag?.tag ? ` · bag ${bag.tag}` : ''}</div>
        {lotQ.isLoading ? (
          <Spinner label="Loading the lot" />
        ) : bag ? (
          <Timeline
            items={bag.weights.map((w) => ({
              id: w.id,
              at: w.takenAt,
              title: (
                <span className="font-mono tabular">
                  {w.valueG !== null ? fmt.weight(w.valueG) : `${w.value ?? '—'} ${w.uom}`}
                  {w.current && <span className="ml-2 rounded-chip bg-ok-wash px-1 text-[11px] text-ok">counts now</span>}
                </span>
              ),
              detail: (
                <>
                  {w.context ?? w.kind} · {w.method ?? 'scale'} · <UserName id={w.takenById} />
                  {w.id === d.disputed_measurement_id && ' · disputed'}
                </>
              ),
              tone: w.current ? 'ok' : w.id === d.disputed_measurement_id ? 'warn' : 'neutral',
            }))}
          />
        ) : (
          <p className="text-[14px] text-muted">The disputed bag's weights are on the lot view.</p>
        )}
      </div>
      {photos.length > 0 && (
        <div>
          <div className="eyebrow mb-2">Re-check photos</div>
          <div className="flex flex-wrap gap-2">
            {photos.map((p, i) => (
              <PhotoViewer key={p} photo={p} alt={`Re-check photo ${i + 1}`} />
            ))}
          </div>
        </div>
      )}
      {blocked && <Banner tone="warn">{blocked}</Banner>}
      <div className="flex flex-wrap gap-2">
        <ActionButton perm="exceptions:resolve" size="sm" leadingIcon={<CheckCircle />} blockedReason={blocked} onClick={() => setDialog('stands')}>
          Value stands
        </ActionButton>
        <ActionButton
          perm="units:correct"
          size="sm"
          variant="secondary"
          leadingIcon={<Scales />}
          blockedReason={blocked ?? (!lot ? 'Loading the lot…' : null)}
          onClick={() => setDialog('correct')}
        >
          Correct weight
        </ActionButton>
      </div>
      <ConfirmDialog
        open={dialog === 'stands'}
        onClose={() => setDialog(null)}
        title="Value stands"
        description="The weight that counts now is kept. The farmer's slip does not change."
        confirmLabel="Settle — value stands"
        loading={resolve.isPending}
        reason={{ label: 'Note', minLength: 3, placeholder: 'What was checked with the farmer' }}
        onConfirm={(note) =>
          resolve.mutateAsync({ id: exception.id, resolutionCode: 'SUPERVISOR_UPHELD', note }).then(() => setDialog(null))
        }
      />
      {lot && (
        <CorrectionDialog
          open={dialog === 'correct'}
          onClose={() => setDialog(null)}
          lot={lot}
          disputeExceptionId={exception.id}
          defaultContainerId={containerId}
          title="Correct weight · settle the dispute"
        />
      )}
    </div>
  )
}
